import axios from 'axios';
import api from './api';
import { API_URL, AUTH_TOKEN_KEY } from './config';

/**
 * Get users with pagination, search and filters
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {string} search - Search term
 * @param {Object} filters - Filter criteria
 * @param {Object} sort - Sort configuration with field and direction
 * @returns {Promise} Promise with users data
 */
export const getUsers = async (
  page = 1, 
  limit = 10, 
  search = '', 
  filters = {}, 
  sort = { field: 'createdAt', direction: 'desc' }
) => {
  // Prepare parameters
  const params = {
    page,
    limit,
    search
  };

  // Add filter parameters
  if (filters.isActive !== undefined && filters.isActive !== null) {
    params.isActive = filters.isActive;
  }

  // Handle array parameters (roles, providers)
  if (filters.roles && filters.roles.length > 0) {
    params.roles = filters.roles;
  }

  if (filters.providers && filters.providers.length > 0) {
    params.providers = filters.providers;
  }

  // Add date range parameters
  if (filters.dateRange) {
    if (filters.dateRange.start) {
      params.createdDateStart = filters.dateRange.start;
    }
    if (filters.dateRange.end) {
      params.createdDateEnd = filters.dateRange.end;
    }
  }

  // Add all=true parameter to fetch all users without pagination (server-side logic applies filters)
  if (filters.all === true || filters.all === 'true') {
    params.all = true;
  }

  // Add sort parameters
  if (sort && sort.field) {
    params.sortBy = sort.field;
    params.sortOrder = sort.direction || 'desc';
  }

  // Make the API request
  const response = await api.get('/api/users', { params });
  return response.data;
};

// Get user by ID
export const getUserById = async (id) => {
  const response = await api.get(`/api/users/${id}`);
  return response.data;
};

// Update user data
export const updateUser = async (id, userData) => {
  const response = await api.put(`/api/users/${id}`, userData);
  return response.data;
};

// Update user role (bulk)
export const updateUserRole = async (id, roles) => {
  const response = await api.patch(`/api/users/${id}/role`, { roles });
  return response.data;
};

// Assign a single role to user (requires role:assign)
export const assignRoleToUser = async (userId, roleId) => {
  const response = await api.post(`/api/users/${userId}/roles`, { roleId });
  return response.data;
};

// Remove a single role from user (requires role:unassign)
export const removeRoleFromUser = async (userId, roleId) => {
  const response = await api.delete(`/api/users/${userId}/roles/${roleId}`);
  return response.data;
};

// Ban user
export const banUser = async (id, reason) => {
  const response = await api.patch(`/api/users/${id}/ban`, { reason });
  return response.data;
};

// Unban user
export const unbanUser = async (id) => {
  const response = await api.patch(`/api/users/${id}/unban`);
  return response.data;
};

// Update user activity status
export const updateUserActivity = async (id, isActive) => {
  const response = await api.patch(`/api/users/${id}/activity`, { isActive });
  return response.data;
};

// Delete user
export const deleteUser = async (id) => {
  const response = await api.delete(`/api/users/${id}`);
  return response.data;
};

// Sync or create user
export const syncOrCreateUser = async (userData) => {
  const response = await api.post('/api/users/sync-or-create', userData);
  return response.data;
};

// Upload user avatar
export const uploadAvatar = async (id, file) => {
  const formData = new FormData();
  formData.append('avatar', file);

  // For FormData we need different headers
  const token = localStorage.getItem(AUTH_TOKEN_KEY);

  const response = await axios.post(
    `${API_URL}/api/users/${id}/avatar`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: token ? `Bearer ${token}` : '',
      },
      withCredentials: true,
    }
  );

  return response.data;
};
